import React from 'react';
import { Flame, ChevronRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import { formatINR } from '@/shared/utils/currency';
import type { DealItem } from './types';

interface PriorityDealsCardProps {
  deals: DealItem[];
  onSelectDeal: (deal: DealItem) => void;
}

export const PriorityDealsCard: React.FC<PriorityDealsCardProps> = ({ deals, onSelectDeal }) => {
  const navigate = useNavigate();

  const getStageBadge = (stage: string) => {
    switch (stage) {
      case 'dispatch':
        return 'bg-success/10 text-success-text border-success/30';
      case 'quotation_sent':
        return 'bg-primary-container text-primary-dim border-primary/30';
      case 'order_confirmed':
        return 'bg-info/10 text-info-text border-info/30';
      case 'sampling':
        return 'bg-warning/10 text-warning-text border-warning/30';
      default:
        return 'bg-surface-variant text-on-surface-variant border-border';
    }
  };

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-outline/80 pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-danger/10 text-danger-text">
            <Flame className="h-4 w-4" />
          </div>
          <div>
            <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
              My Priority Deals
            </span>
            <div className="text-data-xs text-on-surface-subtle">Highest value, open pipeline</div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate(ROUTES.pipeline)}
          className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline"
        >
          <span>View All</span>
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      {/* Deals List */}
      {deals.length === 0 ? (
        <div className="my-6 text-center text-body-sm text-on-surface-subtle py-4">
          No open priority deals found.
        </div>
      ) : (
        <div className="my-2 flex flex-col divide-y divide-border">
          {deals.map((deal) => (
            <div
              key={deal.id}
              onClick={() => onSelectDeal(deal)}
              className="group flex cursor-pointer items-center justify-between py-3 transition hover:bg-surface-variant/80 px-2 rounded-lg"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-surface-container text-on-surface-variant group-hover:bg-primary-container group-hover:text-primary transition mt-0.5">
                  <Flame className="h-3.5 w-3.5" />
                </div>

                <div>
                  <div className="font-semibold text-body-sm text-on-surface group-hover:text-primary transition">
                    {deal.name}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-data-xs text-on-surface-subtle">
                    <span className="font-mono font-semibold tabular-nums text-on-surface">{formatINR(deal.value)}</span>
                    <span>•</span>
                    <span>{deal.city || 'City not recorded'}</span>
                    <span>•</span>
                    <span className="font-mono tabular-nums text-success-text">{deal.probability}% win prob</span>
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-data-xs text-on-surface-variant">
                    <Clock className="h-3 w-3" />
                    <span>{deal.nextFollowup}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`rounded-md border px-2 py-0.5 text-data-xs font-semibold capitalize ${getStageBadge(
                    deal.stage
                  )}`}
                >
                  {deal.stageLabel}
                </span>
                <ChevronRight className="h-4 w-4 text-on-surface-subtle group-hover:translate-x-0.5 group-hover:text-primary transition" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer Total */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Priority Opportunities:</span>
        <span className="font-semibold text-on-surface">
          {deals.length} Deals · {formatINR(deals.reduce((s, d) => s + d.value, 0))}
        </span>
      </div>
    </div>
  );
};
